import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  inject,
  model,
  signal,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogActions,
  MatDialogClose,
  MatDialogContent,
  MatDialogRef,
  MatDialogTitle,
} from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { CompanyInterface } from '@app/interface/company';
import { CompanyApi } from '@app/services/company-api';
import { NgForm } from '@angular/forms';
import { Router } from '@angular/router';
import { ActivatedRoute } from '@angular/router';
import { TypeOptions } from '@app/interface/type-options';
import { MatCardModule } from '@angular/material/card';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatIconModule } from '@angular/material/icon';
import { CountryOptions } from '@app/interface/country-options';
import { BackHeaderComponent } from '@app/back-header/back-header.component';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatExpansionModule } from '@angular/material/expansion';
import { TextFieldModule } from '@angular/cdk/text-field';
import { FormControl } from '@angular/forms';
import { ToastService } from '@app/services/toast.service';

export interface DialogData {
  company_to_register: string;
  name: string;
}

/**
 * @title Dialog Overview
 */
@Component({
  selector: 'new-company.component',
  templateUrl: 'new-company.component.html',
  styleUrl: './new-company.component.scss',
  imports: [
    CommonModule,
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatCardModule,
    MatSelectModule,
    CommonModule,
    MatIconModule,
    MatTooltipModule,
    MatExpansionModule,
    TextFieldModule,
    BackHeaderComponent,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [CompanyApi],

  standalone: true,
})
export class NewCompanyComponent {
  private toast = inject(ToastService);

  // Ritardi dei tooltip, stessi valori di new-invoice.
  showDelay = new FormControl(200);
  hideDelay = new FormControl(1000);

  typeOptions: TypeOptions[] = [
    { code: 'AZIENDA', name: 'Azienda' },
    { code: 'PERSONA', name: 'Persona' },
    { code: 'PUBBLICA AMMINISTRAZIONE', name: 'Pubblica Amministrazione' },
    { code: 'CONDOMINIO', name: 'Condominio' },
  ];

  countryOptions: CountryOptions[] = [
    { code: 'IT', name: 'Italia' },
    { code: 'ES', name: 'Spagna' },
    { code: 'DE', name: 'Germania' },
    { code: 'FR', name: 'Francia' },
  ];

  constructor(
    private companyService: CompanyApi,
    private router: Router,
    private route: ActivatedRoute,
  ) {}

  readonly company_to_register = signal('');
  readonly name = model('');
  readonly dialog = inject(MatDialog);

  newCompany: Partial<CompanyInterface> = {
    id: 1,
    company_name: '',
    organization_id: 1,
    company_id: 1,
    addressee_code: '0000000',
    iva_number: '',
    ateco_code: '',
    address: '',
    email_address: '',
    pec: '',
    c_f: '',
    country: 'IT',
    referent: '',
    internal_code: '',
    cellphone: '',
    telephone: '',
    notes: '',
    type: 'AZIENDA',
    cap: '',
    comune: '',
    province: '',
    city: '',
  };

  addNewCompany(form?: NgForm) {
    // Check if at least one field is filled
    if (!this.validateAtLeastOne()) {
      this.showErrorMessage(
        'Inserire almeno un valore fra P. IVA o Codice Fiscale.',
      );
      return;
    }

    const organization_id = Number(
      this.route.snapshot.paramMap.get('organizationId'),
    );

    console.log('organization_id', organization_id);

    this.newCompany.organization_id = organization_id;

    // validate required fields
    console.log(!this.newCompany.organization_id || !this.newCompany.id);
    console.log(this.newCompany);
    this.companyService
      .createCompany(organization_id, this.newCompany)
      .subscribe({
        next: (createdCompany: CompanyInterface) => {
          // this.companySource.data = [...this.companySource.data, createdCompany];
          this.showSuccessMessage(
            `Cliente ${createdCompany.company_name} aggiunto con successo!`,
          );
          console.log(createdCompany);
          // this.resetForm(form);

          this.backToTable();
        },
        error: (err: any) => {
          console.error('Error creating company:', err);
          this.showErrorMessage('Errore durante la creazione cliente.');
        },
      });
  }

  openDialog(): void {
    const dialogRef = this.dialog.open(NewCompanyDialog, {
      // maxWidth: '100%', // Set width
      data: {
        name: this.name(),
        company_to_register: this.company_to_register(),
      },
    });

    dialogRef.afterClosed().subscribe((result: undefined) => {
      console.log('The dialog was closed');
      if (result !== undefined) {
        this.company_to_register.set(result);
      }
    });
  }

  validateAtLeastOne(): boolean {
    const hasIva =
      !!this.newCompany.iva_number &&
      this.newCompany.iva_number.trim().length > 0;
    const hasCf =
      !!this.newCompany.c_f && this.newCompany.c_f.trim().length > 0;

    return hasIva || hasCf;
  }

  provinceError: string = '';
  partitaIvaError: string = '';
  capsPattern = /^[A-Z]{2}$/; // Exactly 2 uppercase letters
  partitaIvaPattern = /^[0-9]{11}$/; // Exactly 11 numbers

  validatePartitaIva(province: string | undefined): void {
    if (!province || province.trim() === '') {
      this.provinceError = 'La provincia è obbligatoria';
      return;
    }

    if (!this.capsPattern.test(province)) {
      this.provinceError =
        'La provincia deve essere di esattamente 2 lettere maiuscole';
      return;
    }

    this.provinceError = '';
  }

  onPartitaIvaChange(value: string): void {
    // Convert to uppercase
    let upperValue = value.toUpperCase();

    // Allow only numbers
    // value = value.replace(/[^A-Za-z]/g, '');

    // Limit to exactly 2 characters
    if (upperValue.length > 11) {
      upperValue = upperValue.substring(0, 11);
    }

    this.newCompany.iva_number = upperValue;
    this.validatePartitaIva(this.newCompany.iva_number);
  }

  onPartitaIvaInput(event: any): void {
    // Get the input value
    let value = event.target.value.toUpperCase();

    // Allow only numbers
    // value = value.replace(/[^A-Za-z]/g, '');

    // Limit to exactly 2 characters
    if (value.length > 11) {
      value = value.substring(0, 11);
    }

    this.newCompany.iva_number = value;
    this.validatePartitaIva(this.newCompany.iva_number);
  }

  validateProvince(partitaIva: string | undefined): void {
    // Handle undefined or empty string
    if (partitaIva) {
      if (!this.partitaIvaPattern.test(partitaIva)) {
        this.partitaIvaError = 'La Partita Iva deve essere di 11 cifre';
        return;
      }

      this.partitaIvaError = '';
    }
  }

  onProvinceInput(event: any): void {
    // Get the input value
    let value = event.target.value.toUpperCase();

    // Allow only letters
    value = value.replace(/[^A-Z]/g, '');

    // Limit to exactly 2 characters
    if (value.length > 2) {
      value = value.substring(0, 2);
    }

    this.newCompany.province = value;
    this.validateProvince(this.newCompany.province);
  }

  onProvinceChange(value: string): void {
    // Convert to uppercase
    let upperValue = value.toUpperCase();

    // Allow only letters
    upperValue = upperValue.replace(/[^A-Z]/g, '');

    // Limit to exactly 2 characters
    if (upperValue.length > 2) {
      upperValue = upperValue.substring(0, 2);
    }

    this.newCompany.province = upperValue;
    this.validateProvince(this.newCompany.province);
  }

  backToTable(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/company_table`]);
  }

  backToDashboard(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/dashboard`]);
  }

  private showSuccessMessage(message: string): void {
    this.toast.success(message);
  }

  private showErrorMessage(message: string): void {
    this.toast.error(message);
  }
}

@Component({
  selector: 'new-company-dialog',
  templateUrl: 'new-company-dialog.html',
  styleUrl: './new-company.component.scss',
  imports: [
    MatFormFieldModule,
    MatInputModule,
    FormsModule,
    MatButtonModule,
    MatDialogTitle,
    MatDialogContent,
    MatDialogActions,
    MatDialogClose,
    MatSelectModule,
    MatFormFieldModule,
    MatOptionModule,
    CommonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  providers: [CompanyApi],
})
export class NewCompanyDialog {
  private toast = inject(ToastService);

  // Ritardi dei tooltip, stessi valori di new-invoice.
  showDelay = new FormControl(200);
  hideDelay = new FormControl(1000);

  typeOptions: TypeOptions[] = [
    { code: 'AZIENDA', name: 'Azienda' },
    { code: 'PERSONA', name: 'Persona' },
    { code: 'PUBBLICA AMMINISTRAZIONE', name: 'Pubblica Amministrazione' },
    { code: 'CONDOMINIO', name: 'Condominio' },
  ];

  countryOptions: CountryOptions[] = [
    { code: 'IT', name: 'Italia' },
    { code: 'ES', name: 'Spagna' },
    { code: 'DE', name: 'Germania' },
    { code: 'FR', name: 'Francia' },
  ];

  newCompany: Partial<CompanyInterface> = {
    id: 1,
    company_name: '',
    organization_id: 0,
    company_id: 1,
    addressee_code: '0000000',
    iva_number: '',
    ateco_code: '',
    address: '',
    email_address: '',
    pec: '',
    c_f: '',
    country: 'IT',
    referent: '',
    internal_code: '',
    cellphone: '',
    telephone: '',
    notes: '',
    type: 'AZIENDA',
    cap: '',
    comune: '',
    province: '',
    city: '',
  };

  constructor(
    private companyService: CompanyApi,
    private route: ActivatedRoute,
    private router: Router,
  ) {}

  readonly dialogRef = inject(MatDialogRef<NewCompanyDialog>);
  readonly data = inject<DialogData>(MAT_DIALOG_DATA);
  readonly company_to_register = model(this.data.company_to_register);

  onNoClick(): void {
    this.dialogRef.close();
  }

  // different method since paramMap isn't wokring here
  extractOrganizationIdFromUrl(): number | null {
    const url = this.router.url;

    // Remove query parameters and hash if present
    const cleanUrl = url.split('?')[0].split('#')[0];

    // Match the first number in the URL path
    const match = cleanUrl.match(/^\/(\d+)/);

    if (match && match[1]) {
      const orgId = Number(match[1]);
      console.log('Extracted organizationId:', orgId);
      return orgId;
    }

    console.warn('No numeric organization ID found at the start of URL');
    return null;
  }

  validateAtLeastOne(): boolean {
    const hasIva =
      !!this.newCompany.iva_number &&
      this.newCompany.iva_number.trim().length > 0;
    const hasCf =
      !!this.newCompany.c_f && this.newCompany.c_f.trim().length > 0;

    return hasIva || hasCf;
  }

  provinceError: string = '';
  partitaIvaError: string = '';
  capsPattern = /^[A-Z]{2}$/; // Exactly 2 uppercase letters
  partitaIvaPattern = /^[0-9]{11}$/; // Exactly 2 uppercase letters

  validatePartitaIva(province: string | undefined): void {
    if (!province || province.trim() === '') {
      this.provinceError = 'La provincia è obbligatoria';
      return;
    }

    if (!this.capsPattern.test(province)) {
      this.provinceError =
        'La provincia deve essere di esattamente 2 lettere maiuscole';
      return;
    }

    this.provinceError = '';
  }

  onPartitaIvaChange(value: string): void {
    // Convert to uppercase
    let upperValue = value.toUpperCase();

    // Allow only numbers
    upperValue = upperValue.replace(/[^0-9]/g, '');

    // Limit to exactly 2 characters
    if (upperValue.length > 11) {
      upperValue = upperValue.substring(0, 11);
    }

    this.newCompany.iva_number = upperValue;
    this.validatePartitaIva(this.newCompany.iva_number);
  }

  onPartitaIvaInput(event: any): void {
    // Get the input value
    let value = event.target.value.toUpperCase();

    // Allow only letters
    value = value.replace(/[^0-9]/g, '');

    // Limit to exactly 2 characters
    if (value.length > 11) {
      value = value.substring(0, 11);
    }

    this.newCompany.iva_number = value;
    this.validateProvince(this.newCompany.iva_number);
  }

  validateProvince(partitaIva: string | undefined): void {
    // Handle undefined or empty string
    if (partitaIva) {
      if (!this.partitaIvaPattern.test(partitaIva)) {
        this.partitaIvaError = 'La Partita Iva deve essere di 11 cifre';
        return;
      }

      this.partitaIvaError = '';
    }
  }

  addNewCompany(form?: NgForm) {
    if (!this.validateAtLeastOne()) {
      this.showErrorMessage(
        'Inserire almeno un valore fra P. IVA o Codice Fiscale.',
      );
      return;
    }

    // Try to get organizationId from URL first
    let organization_id = this.extractOrganizationIdFromUrl();

    // If not found in URL, fall back to route params
    if (!organization_id) {
      organization_id = Number(
        this.route.snapshot.paramMap.get('organizationId'),
      );
      console.log('OrganizationId from route params:', organization_id);
    }

    // If still not found, try alternative param names
    if (!organization_id || isNaN(organization_id)) {
      organization_id = Number(this.route.snapshot.paramMap.get('id'));
      console.log('OrganizationId as "id":', organization_id);
    }

    this.newCompany.organization_id = organization_id;

    // Validate required fields - either iva_number or c_f must be provided
    // if (this.newCompany.iva_number === '') {
    //   if (this.newCompany.c_f === '') {
    //     this.showErrorMessage(
    //       'È necessario fornire almeno la Partita IVA o il Codice Fiscale'
    //     );
    //     return; // Stop execution if validation fails
    //   }
    // }

    // validate required fields
    console.log(!this.newCompany.organization_id || !this.newCompany.id);
    console.log(this.newCompany);
    this.companyService
      .createCompany(organization_id, this.newCompany)
      .subscribe({
        next: (createdCompany: CompanyInterface) => {
          // this.companySource.data = [...this.companySource.data, createdCompany];
          this.showSuccessMessage(
            `Cliente ${createdCompany.company_name} aggiunto con successo!`,
          );

          // Close the dialog and return the created company to the caller.
          // Navigation is handled by whoever opened the dialog via afterClosed().
          this.dialogRef.close(createdCompany);

          // this.resetForm(form);
        },
        error: (err: any) => {
          console.error('Error creating company:', err);
          this.showErrorMessage('Errore nella creazione cliente');
        },
      });
  }

  onProvinceInput(event: any): void {
    // Get the input value
    let value = event.target.value.toUpperCase();

    // Allow only letters
    value = value.replace(/[^A-Z]/g, '');

    // Limit to exactly 2 characters
    if (value.length > 2) {
      value = value.substring(0, 2);
    }

    this.newCompany.province = value;
    this.validateProvince(this.newCompany.province);
  }

  onProvinceChange(value: string): void {
    // Convert to uppercase
    let upperValue = value.toUpperCase();

    // Allow only letters
    upperValue = upperValue.replace(/[^A-Z]/g, '');

    // Limit to exactly 2 characters
    if (upperValue.length > 2) {
      upperValue = upperValue.substring(0, 2);
    }

    this.newCompany.province = upperValue;
    this.validateProvince(this.newCompany.province);
  }

  backToTable(): void {
    const org_id = this.route.snapshot.paramMap.get('organizationId');
    this.router.navigate([`/${org_id}/company_table`]);
  }

  private showSuccessMessage(message: string): void {
    this.toast.success(message);
  }

  private showErrorMessage(message: string): void {
    this.toast.error(message);
  }
}
